/**
 * Utilities for handling image URLs
 */

/**
 * Check if a URL is absolute (starts with http:// or https://)
 */
export function isAbsoluteUrl(url: string): boolean {
  return /^https?:\/\//i.test(url);
}

/**
 * Check browser support for image formats
 */
export function checkImageFormatSupport(): { webp: boolean; avif: boolean } {
  // Modern browsers support WebP, so we assume true by default
  return { webp: true, avif: false };
}

/**
 * Get optimized image URL with fallback support
 */
export function getOptimalImageUrl(
  url: string,
  options: {
    width?: number;
    quality?: number;
  } = {}
): string {
  if (!url) return '';

  // Convert to WebP URL
  let optimalUrl = url.replace('/pokemon-images/', '/pokemon-images-webp/')
                     .replace('.png', '.webp');

  // Add transformation parameters
  const params = new URLSearchParams();
  if (options.width) params.append('width', options.width.toString());
  if (options.quality) params.append('quality', options.quality.toString());
  
  // Only add params if we have any
  const paramsString = params.toString();
  if (paramsString) {
    optimalUrl += `?${paramsString}`;
  }

  return optimalUrl;
}

/**
 * Get a fallback URL when image loading fails
 */
export function getFallbackImageUrl(url: string): string {
  if (!url) return '';
  
  // Try different fallbacks in order
  const fallbacks = [
    // 1. Try PNG version of WebP
    {
      test: (u: string) => u.includes('.webp'),
      transform: (u: string) => u.replace('.webp', '.png')
                                .replace('/pokemon-images-webp/', '/pokemon-images/')
    },
    // 2. Try large version of small
    {
      test: (u: string) => u.includes('_small.'),
      transform: (u: string) => u.replace('_small.', '_large.')
    },
    // 3. Try small version of large
    {
      test: (u: string) => u.includes('_large.'),
      transform: (u: string) => u.replace('_large.', '_small.')
    },
    // 4. Try alternate ID format
    {
      test: (u: string) => u.includes('_small') || u.includes('_large'),
      transform: (u: string) => u.replace(/_small|_large/, '')
    }
  ];

  // Try each fallback in order
  for (const fallback of fallbacks) {
    if (fallback.test(url)) {
      const newUrl = fallback.transform(url);
      // Remove any query parameters
      return newUrl.split('?')[0];
    }
  }
  
  // If no fallbacks work, return original URL without query params
  return url.split('?')[0];
}